import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useBooking } from '../hooks/useBooking';
import { hashSHA256, checkApprovedBooker } from '../utils/firebase';
import { formatTimeForDisplay } from '../utils/availability';
import { formatPrice } from '../types';
import { PhoneNumberInput } from '../components/PhoneNumberInput';
import { BookingStepper } from '../components/BookingStepper';

export function CustomerDetailsPage() {
  const navigate = useNavigate();
  const {
    bookingState,
    setCustomerInfo,
    businessListing,
    setIsApprovedBooker,
  } = useBooking();


  const { selectedService, selectedDate, selectedTime, customerInfo } = bookingState;

  // No local state needed for phone/countryCode anymore as they are in customerInfo

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Pre-check approved booker as soon as phone is complete ──
  // Direct async call from onChange — no useCallback/useEffect needed
  const lastCheckedPhone = useRef('');

  const checkApproval = async (phoneDigits: string) => {
    if (!businessListing || !businessListing.id || !['selectClients', 'selectGroups'].includes(businessListing.confirmation_mode)) return;
    if (phoneDigits.length < 10) return;
    if (lastCheckedPhone.current === phoneDigits) return;
    lastCheckedPhone.current = phoneDigits;

    try {
      const phoneHash = await hashSHA256(phoneDigits);
      const emailValue = customerInfo.email?.trim().toLowerCase() || '';
      const emailHash = emailValue ? await hashSHA256(emailValue) : null;
      const approved = await checkApprovedBooker(businessListing.id, emailHash, phoneHash);
      setIsApprovedBooker(approved);
    } catch (err) {
      console.error('Approval check error:', err);
    }
  };

  if (!selectedService || !selectedDate || !selectedTime) {
    navigate('/book');
    return null;
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!customerInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!customerInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!customerInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(customerInfo.phone)) {
      newErrors.phone = 'Please enter a 10-digit phone number';
    }

    if (customerInfo.email && customerInfo.email.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      navigate('/book/forms');
    }
  };

  const handleInputChange = (field: keyof typeof customerInfo, value: string) => {
    setCustomerInfo({ [field]: value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };



  const showContinue = customerInfo.firstName.trim().length > 0 &&
    customerInfo.lastName.trim().length > 0 &&
    (customerInfo.phone.trim().length ?? 0) > 0;

  return (
    <div className="min-h-screen bg-site-bg text-site-text pb-24">
      {/* Focused fields in the site's primary color */}
      <style>{`
        .accent-focus:focus { border-color: var(--site-primary) !important; }
      `}</style>

      {/* Header with Stepper */}
      <BookingStepper currentStep={3} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Booking Summary */}
        <div className="bg-site-bg rounded-2xl border border-site-border p-6 mb-6">
          <h2 className="text-lg font-semibold text-site-text mb-4">
            Booking Summary
          </h2>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-site-muted">Service:</span>
              <span className="font-medium text-site-text">{selectedService.name}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-site-muted">Date:</span>
              <span className="font-medium text-site-text">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-site-muted">Time:</span>
              <span className="font-medium text-site-text">
                {formatTimeForDisplay(selectedTime)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-site-muted">Duration:</span>
              <span className="font-medium text-site-text">
                {selectedService.duration_minutes} minutes
              </span>
            </div>

            <div className="flex justify-between pt-2 border-t border-site-border">
              <span className="text-site-muted">Price:</span>
              <span className="font-semibold text-site-text text-lg">
                {formatPrice(selectedService.price_cents, selectedService.currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Information Form */}
        <form id="customer-details-form" onSubmit={handleSubmit} className="bg-site-bg rounded-2xl border border-site-border p-6">
          <h2 className="text-lg font-semibold text-site-text mb-6">
            Contact Information
          </h2>

          <div className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-site-text mb-2">
                  First Name <span className="text-site-danger">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={customerInfo.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-xl border-2 transition-colors
                    ${errors.firstName
                      ? 'border-site-danger/40 focus:border-site-danger'
                      : 'border-site-border accent-focus'
                    }
                    focus:outline-none
                  `}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="text-site-danger text-sm mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-site-text mb-2">
                  Last Name <span className="text-site-danger">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={customerInfo.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`
                    w-full px-4 py-3 rounded-xl border-2 transition-colors
                    ${errors.lastName
                      ? 'border-site-danger/40 focus:border-site-danger'
                      : 'border-site-border accent-focus'
                    }
                    focus:outline-none
                  `}
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <p className="text-site-danger text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-site-text mb-2">
                Phone Number <span className="text-site-danger">*</span>
              </label>
              <PhoneNumberInput
                countryCode={customerInfo.countryCode}
                phone={customerInfo.phone}
                onCountryCodeChange={(code) => handleInputChange('countryCode', code)}
                onPhoneChange={(digits) => handleInputChange('phone', digits)}
                onPhoneComplete={checkApproval}
                error={errors.phone}
                inputClassName="accent-focus"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-site-text mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={customerInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onBlur={() => {
                  // Re-check approval when email changes (email might be an approved hash)
                  const phoneDigits = customerInfo.phone?.replace(/\D/g, '') || '';
                  if (phoneDigits.length === 10) {
                    lastCheckedPhone.current = ''; // force re-check with updated email
                    checkApproval(phoneDigits);
                  }
                }}
                className={`
                  w-full px-4 py-3 rounded-xl border-2 transition-colors
                  ${errors.email
                    ? 'border-site-danger/40 focus:border-site-danger'
                    : 'border-site-border accent-focus'
                  }
                  focus:outline-none
                `}
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="text-site-danger text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-site-text mb-2">
                Special Requests or Notes
              </label>
              <textarea
                id="notes"
                value={customerInfo.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 rounded-xl border-2 border-site-border accent-focus focus:outline-none transition-colors resize-none"
                placeholder="Any special requests or information we should know?"
              />
            </div>
          </div>

          {/* Policies */}
          {businessListing?.cancellation_policy_text && (
            <div className="mt-6 p-4 bg-site-bg rounded-xl">
              <p className="text-xs text-site-muted">
                <span className="font-semibold">Cancellation Policy:</span>{' '}
                {businessListing.cancellation_policy_text}
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Sticky Continue Button */}
      {showContinue && (
        <div className="fixed bottom-0 inset-x-0 z-30 px-4 py-3">
          <div className="max-w-4xl mx-auto">
            <button
              type="submit"
              form="customer-details-form"
              className="w-full bg-site-primary text-site-on-primary border border-site-primary-outline font-semibold py-3.5 rounded-full transition-colors"
            >
              Continue to Confirmation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
